import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { db, getFirebaseImageUrl } from "../firebase";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import { FileUpload } from "primereact/fileupload";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import { Toast } from "primereact/toast";
import { getDesignDiscountsMap, saveDesignDiscount, deleteDesignDiscountByDesignId } from "../designDiscountService";
import { getActiveDiscount } from "../discountService";
import { mainCategories, blindSubCategories, nameToSlug } from "../categoryMappings";

const categoryOptions = mainCategories
  .filter((c) => c !== "All")
  .map((c) => ({ label: c, value: c }));

const blindSubCategoryOptions = blindSubCategories.map((name) => ({
  label: name,
  value: nameToSlug[name],
}));

const defaultFormState = {
  code: "",
  name: "",
  price: "",
  category: "",
  subCategory: "",
  order: Date.now(),
  imageFile: null,
  image: null,
  discountIsPercentage: true,
  discountValue: null,
};

const discountTypeOptions = [
  { label: "Percentage", value: true },
  { label: "Fixed (LKR)", value: false },
];

function formatDiscountLabel(d) {
  if (!d || (d.value === 0 && d.value !== undefined)) return null;
  return d.isPercentage ? `${d.value}%` : `LKR ${d.value}`;
}

const Items = () => {
  const [products, setProducts] = useState([]);
  const [discountsMap, setDiscountsMap] = useState({});
  const [commonDiscount, setCommonDiscount] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formValues, setFormValues] = useState({ ...defaultFormState });
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const confirmingDelete = useRef(false);
  const toast = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    getActiveDiscount().then((d) => setCommonDiscount(d));
  }, []);

  const loadData = async () => {
    const data = [];
    const querySnapshot = await getDocs(collection(db, "Designs"));
    querySnapshot.forEach((docSnap) => {
      if (docSnap.exists && docSnap.data().Status) {
        const record = {
          id: docSnap.id,
          code: docSnap.data().Code,
          name: docSnap.data().Name,
          image: docSnap.data().Image,
          price: docSnap.data().Price,
          order: docSnap.data().Order,
          category: docSnap.data().Category || "",
          subCategory: docSnap.data().SubCategory || "",
        };
        data.push(record);
      }
    });
    const ids = data.map((p) => p.id);
    let map = {};
    try {
      map = await getDesignDiscountsMap(ids);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Discount fetch failed",
        detail: "Could not load item-specific discounts. Showing seasonal discount only.",
        life: 8000,
      });
    }
    setDiscountsMap(map);
    setProducts(data.map((p) => ({ ...p, designDiscount: map[p.id] || null })));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormValues((prevValues) => ({
        ...prevValues,
        image: file.name,
        imageFile: file,
      }));
    };
    reader.readAsDataURL(file);
  };

  const openAddDialog = () => {
    const base = { ...defaultFormState, order: Date.now() };
    if (commonDiscount && commonDiscount.value > 0) {
      base.discountIsPercentage = commonDiscount.isPercentage;
      base.discountValue = commonDiscount.value;
    }
    setFormValues(base);
    setIsEditing(false);
    setShowDialog(true);
  };

  const uploadImageAndGetURL = async (file) => {
    const storage = getStorage();
    const storageRef = ref(storage, `${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);
    return new Promise((resolve, reject) => {
      uploadTask.on("state_changed", null, reject, async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(downloadURL);
      });
    });
  };

  const addOrUpdateProduct = async () => {
    if (
      !formValues.name ||
      !formValues.code ||
      !formValues.price ||
      !formValues.order ||
      !formValues.image
    ) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "All fields are required",
        life: 3000,
      });
      return;
    }

    if (formValues.category === "Blinds" && !formValues.subCategory) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Pick a blind sub-category (Zebra, Roller, Vertical, etc.)",
        life: 3000,
      });
      return;
    }

    if (formValues.imageFile) {
      await uploadImageAndGetURL(formValues.imageFile);
    }

    const productData = {
      Code: formValues.code,
      Name: formValues.name,
      Price: parseFloat(formValues.price),
      Order: parseInt(formValues.order),
      Image: formValues.image,
      Category: formValues.category,
      SubCategory: formValues.category === "Blinds" ? formValues.subCategory : "",
      Status: true,
    };

    let designIdToUse;
    if (isEditing && selectedProduct) {
      await setDoc(doc(db, "Designs", selectedProduct.id), productData);
      designIdToUse = selectedProduct.id;
    } else {
      const docRef = await addDoc(collection(db, "Designs"), productData);
      designIdToUse = docRef.id;
    }

    const hasDiscount =
      formValues.discountValue != null &&
      formValues.discountValue !== "" &&
      Number(formValues.discountValue) > 0;
    if (hasDiscount) {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.current.show({
          severity: "error",
          summary: "Login required",
          detail: "You must log in first to save discounts. Go to /#/admin-login.",
          sticky: true,
        });
        return;
      }
      try {
        await saveDesignDiscount({
          designId: designIdToUse,
          designName: formValues.name || null,
          isPercentage: formValues.discountIsPercentage,
          value: Number(formValues.discountValue),
        });
        toast.current.show({
          severity: "success",
          summary: "Saved",
          detail: "Item and discount saved successfully.",
          life: 3000,
        });
      } catch (err) {
        const status = err.response?.status;
        let detail = err.response?.data?.title || err.response?.data?.detail;
        if (!detail) {
          if (status === 401) detail = "Session expired. Please log in again at /#/admin-login.";
          else if (status === 403) detail = "Your account does not have permission. Log in with an admin account.";
          else if (status >= 500) detail = "Server error. Try again later.";
          else if (!status || err.code === "ERR_NETWORK") detail = "Backend may be unavailable. Ensure the API is running.";
          else detail = "Backend may be unavailable or login required.";
        }
        toast.current.show({
          severity: "error",
          summary: "Discount NOT saved",
          detail,
          sticky: true,
        });
      }
    } else {
      toast.current.show({
        severity: "success",
        summary: "Saved",
        detail: "Item saved successfully.",
        life: 3000,
      });
    }

    await loadData();
    setShowDialog(false);
  };

  const editProduct = (product) => {
    setIsEditing(true);
    setSelectedProduct(product);
    const d = product.designDiscount ?? commonDiscount;
    setFormValues({
      code: product.code,
      name: product.name,
      price: product.price,
      category: product.category || "",
      subCategory: product.subCategory || "",
      order: product.order,
      imageFile: null,
      image: product.image,
      discountIsPercentage: d ? d.isPercentage : true,
      discountValue: d && d.value > 0 ? d.value : null,
    });
    setShowDialog(true);
  };

  const deleteProduct = async (product) => {
    const storage = getStorage();
    const storageRef = ref(storage, `images/${product.image}`);
    try {
      await deleteObject(storageRef);
    } catch {
      // image may already be deleted
    }
    try {
      await deleteDesignDiscountByDesignId(product.id);
    } catch {
      // no discount record to clean up
    }
    await deleteDoc(doc(db, "Designs", product.id));
    loadData();
  };

  const confirmDeleteProduct = (product) => {
    confirmDialog({
      message: "Are you sure you want to delete this item?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => deleteProduct(product),
      reject: () => (confirmingDelete.current = false),
    });
  };

  const clearForm = () => {
    setFormValues({ ...defaultFormState, order: Date.now() });
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setFilters({ ...filters, global: { ...filters.global, value } });
  };

  const renderDialogContent = () => (
    <div className="flex gap-4" style={{ width: "600px" }}>
      <div
        className="flex flex-column align-items-center"
        style={{ width: "250px" }}
      >
        <label>Current Image</label>
        {formValues.imageFile ? (
          <img
            src={URL.createObjectURL(formValues.imageFile)}
            alt="Selected"
            style={{ width: "100%", height: "auto", borderRadius: "8px" }}
          />
        ) : formValues.image ? (
          <img
            src={getFirebaseImageUrl(formValues.image)}
            alt="Selected"
            style={{ width: "100%", height: "auto", borderRadius: "8px" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "300px",
              backgroundColor: "#e0e0e0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <span>No Image</span>
          </div>
        )}
      </div>

      <div className="flex flex-column gap-2" style={{ width: "400px" }}>
        <div className="flex flex-column">
          <label>Item Name:</label>
          <InputText
            name="name"
            value={formValues.name}
            onChange={handleInputChange}
            className="p-inputtext-sm"
            required
          />
        </div>
        <div className="flex flex-column">
          <label>Item Code:</label>
          <InputText
            name="code"
            value={formValues.code}
            onChange={handleInputChange}
            className="p-inputtext-sm"
            required
          />
        </div>
        <div className="flex flex-column">
          <label>Category (optional):</label>
          <Dropdown
            value={formValues.category}
            options={categoryOptions}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                category: e.value,
                subCategory: e.value === "Blinds" ? prev.subCategory : "",
              }))
            }
            placeholder="Select category (optional)"
            showClear
            className="p-inputtext-sm"
          />
        </div>
        {formValues.category === "Blinds" && (
          <div className="flex flex-column">
            <label>Blind Type:</label>
            <Dropdown
              value={formValues.subCategory}
              options={blindSubCategoryOptions}
              onChange={(e) =>
                setFormValues((prev) => ({ ...prev, subCategory: e.value }))
              }
              placeholder="Select blind type"
              className="p-inputtext-sm"
              required
            />
          </div>
        )}
        <div className="flex flex-column">
          <label>{formValues.category === "Blinds" ? "Price Per Sq Ft:" : "Price Per Meter:"}</label>
          <InputNumber
            name="price"
            value={formValues.price}
            onValueChange={(e) =>
              setFormValues((prev) => ({ ...prev, price: e.value }))
            }
            mode="currency"
            currency="LKR"
            locale="en-US"
            className="p-inputtext-sm"
            required
          />
        </div>
        <div className="flex flex-column">
          <label>Discount (optional)</label>
          <p className="text-color-secondary text-sm mt-0 mb-2">
            Default: common discount from admin backend (e.g. demo/seasonal). Shown as a bubble on the gallery and applied in cart. Change here to override for this item only.
          </p>
          <div className="flex gap-2 align-items-center flex-wrap">
            <Dropdown
              value={formValues.discountIsPercentage}
              options={discountTypeOptions}
              onChange={(e) =>
                setFormValues((prev) => ({ ...prev, discountIsPercentage: e.value }))
              }
              placeholder="Type"
              className="p-inputtext-sm"
              style={{ minWidth: "120px" }}
            />
            <InputNumber
              value={formValues.discountValue}
              onValueChange={(e) =>
                setFormValues((prev) => ({ ...prev, discountValue: e.value }))
              }
              placeholder={formValues.discountIsPercentage ? "e.g. 10" : "e.g. 500"}
              min={0}
              max={formValues.discountIsPercentage ? 100 : undefined}
              mode={formValues.discountIsPercentage ? "decimal" : "decimal"}
              suffix={formValues.discountIsPercentage ? "%" : " LKR"}
              className="p-inputtext-sm"
              style={{ minWidth: "120px" }}
            />
          </div>
        </div>
        <div className="flex flex-column">
          <label>Order:</label>
          <InputNumber
            name="order"
            value={formValues.order}
            onValueChange={(e) =>
              setFormValues((prev) => ({ ...prev, order: e.value }))
            }
            className="p-inputtext-sm"
            required
          />
        </div>
        <div className="flex flex-column">
          <label>Upload Image:</label>
          <FileUpload
            mode="basic"
            accept="image/*"
            customUpload
            uploadHandler={handleFileChange}
            auto
            chooseLabel="Select File"
            className="p-inputtext-sm"
            required
          />
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            label="Save"
            onClick={addOrUpdateProduct}
            className="p-button-outlined btn-outlined"
          />
          <Button
            label="Clear"
            onClick={clearForm}
            className="p-button-outlined btn-outlined"
          />
        </div>
      </div>
    </div>
  );

  const imageBodyTemplate = (rowData) => (
    <img
      src={getFirebaseImageUrl(rowData.image)}
      alt={rowData.name}
      style={{ width: "50px", height: "50px", objectFit: "cover" }}
    />
  );

  return (
    <div
      className="flex flex-column gap-3 p-5"
      style={{ minHeight: "calc(100vh - 419px)" }}
    >
      <Toast ref={toast} />
      <div className="flex gap-2 justify-content-between">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            className="p-inputtext-sm"
            type="search"
            onInput={onGlobalFilterChange}
            placeholder="Search"
          />
        </span>
        <div className="flex gap-2">
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            onClick={loadData}
            className="p-button-outlined p-button-sm btn-outlined"
          />
          <Button
            label="Add New Item"
            onClick={openAddDialog}
            className="p-button-outlined p-button-sm btn-outlined"
          />
        </div>
      </div>
      <DataTable
        value={products}
        paginator
        rows={20}
        rowsPerPageOptions={[20, 50, 100]}
        filters={filters}
        globalFilterFields={["name", "code", "price"]}
        size="small"
      >
        <Column field="image" header="Image" body={imageBodyTemplate} />
        <Column sortable field="order" header="Order" />
        <Column sortable field="code" header="Code" />
        <Column sortable field="name" header="Name" />
        <Column
          sortable
          field="price"
          header="Price"
          body={(rowData) => `${rowData.price.toFixed(2)} LKR`}
          style={{ textAlign: "right" }}
        />
        <Column
          header="Discount"
          body={(rowData) => {
            const effective = rowData.designDiscount ?? commonDiscount;
            const label = formatDiscountLabel(effective);
            return label ? <span className="font-semibold">{label}</span> : "—";
          }}
        />
        <Column
          body={(rowData) => (
            <div className="flex gap-2">
              <Button
                label="Edit"
                icon="pi pi-pencil"
                onClick={() => editProduct(rowData)}
                className="p-button-text"
              />
              <Button
                label="Delete"
                icon="pi pi-trash"
                onClick={() => confirmDeleteProduct(rowData)}
                className="p-button-text p-button-danger"
              />
            </div>
          )}
          header="Actions"
        />
      </DataTable>
      <Dialog
        visible={showDialog}
        onHide={() => setShowDialog(false)}
        draggable={false}
        modal={false}
        header={isEditing ? "Edit Item" : "Add New Item"}
      >
        {renderDialogContent()}
      </Dialog>
    </div>
  );
};

export default Items;
